import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Heart, MessageCircle, Bookmark, Send, Grid3x3, Play, Tag } from 'lucide-react';
import BlogSketch from './BlogSketch';
import { IG_PROFILE, IG_HIGHLIGHTS } from '../data/instagramPlan';
import { CALENDAR_MONTHS } from '../data/editorialCalendar';
import { useSeo } from '../hooks/useSeo';

/**
 * InstagramPreview — realistic mockup of the @lempole.wine profile.
 * Feeds directly from the 3-month editorial calendar so the profile
 * grid mirrors what will actually be published.
 */

const PILLAR_LABEL = {
  manifesto: 'Manifesto',
  terroir:   'Terroir',
  vigneron:  'Vigneron',
  prodotto:  'Prodotto',
};

// Default hashtag sets per pillar — used when a calendar post doesn't ship its own
const PILLAR_TAGS = {
  manifesto: ['#lempole', '#slowwine', '#winemanifesto', '#slothspirit', '#chianticlassico'],
  terroir:   ['#lamole', '#chianticlassico', '#terroir', '#viticolturaeroica', '#600m'],
  vigneron:  ['#vigneron', '#winemaker', '#lamolelife', '#lempole', '#chianti'],
  prodotto:  ['#lempole', '#rosatoitaliano', '#craftwine', '#limitedbottling', '#sangiovese'],
};

// Convert calendar post shape → IG post shape (caption + hashtags)
function toIgPost(cp) {
  return {
    id: cp.day + '-' + cp.title,
    pillar: cp.pillar,
    // PostThumb reads media.kind — merge the top-level kind into media
    media: { kind: cp.kind, ...cp.media },
    date: cp.day,
    caption: [cp.title, '', cp.hook],
    hashtags: PILLAR_TAGS[cp.pillar] || [],
  };
}

function PostThumb({ post, onOpen }) {
  const { media } = post;

  const inner = (() => {
    if (media.kind === 'photo') {
      return <img src={media.src} alt={post.caption[0]} loading="lazy" />;
    }
    if (media.kind === 'sketch') {
      return (
        <div className="ig-thumb__sketch">
          <BlogSketch slug={media.slug} />
        </div>
      );
    }
    // quote
    return (
      <div className="ig-thumb__quote">
        <span className="ig-thumb__quote-text font-display-italic">{media.text}</span>
        <span className="ig-thumb__quote-by font-caveat">{media.by}</span>
      </div>
    );
  })();

  return (
    <button
      type="button"
      className={`ig-thumb ig-thumb--${media.kind}`}
      onClick={() => onOpen(post)}
      data-testid={`ig-post-${post.id}`}
      aria-label={post.caption[0]}
    >
      {inner}
      <span className="ig-thumb__pillar font-kicker">{PILLAR_LABEL[post.pillar]}</span>
    </button>
  );
}

function PostModal({ post, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!post) return null;
  const { media } = post;

  const visual = (() => {
    if (media.kind === 'photo') return <img src={media.src} alt="" />;
    if (media.kind === 'sketch') return <div className="ig-modal__sketch"><BlogSketch slug={media.slug} /></div>;
    return (
      <div className="ig-modal__quote">
        <span className="ig-modal__quote-text font-display-italic">{media.text}</span>
        <span className="ig-modal__quote-by font-caveat">{media.by}</span>
      </div>
    );
  })();

  return (
    <div className="ig-modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="ig-modal__card" onClick={(e) => e.stopPropagation()}>
        <div className="ig-modal__visual">{visual}</div>
        <div className="ig-modal__side">
          <header className="ig-modal__head">
            <img src={IG_PROFILE.avatar} alt="" className="ig-modal__avatar" />
            <div>
              <p className="ig-modal__handle">{IG_PROFILE.handle}</p>
              <p className="ig-modal__meta">Lamole · Chianti Classico</p>
            </div>
            <button className="ig-modal__close" onClick={onClose} aria-label="Chiudi" data-testid="ig-modal-close">×</button>
          </header>
          <div className="ig-modal__body">
            <p className="ig-modal__pillar font-kicker">
              <span className="dot" /> {PILLAR_LABEL[post.pillar]}
            </p>
            <div className="ig-modal__caption">
              <span className="ig-modal__handle-inline">{IG_PROFILE.handle}</span>{' '}
              {post.caption.map((line, i) => (
                <span key={i}>{line}{i < post.caption.length - 1 ? <br/> : null}</span>
              ))}
            </div>
            <p className="ig-modal__tags">
              {post.hashtags.join(' ')}
            </p>
          </div>
          <footer className="ig-modal__actions">
            <Heart size={22} />
            <MessageCircle size={22} />
            <Send size={22} />
            <Bookmark size={22} className="ml-auto" />
          </footer>
        </div>
      </div>
    </div>
  );
}

export default function InstagramPreview() {
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState('grid');

  useSeo({
    title: 'Lempole — Instagram profile preview',
    description: 'Mockup del profilo Instagram @lempole.wine: 15 post, Highlights e strategia editoriale.',
    canonical: 'https://lempole.com/instagram-preview',
    type: 'website',
  });

  const filtered = useMemo(() => {
    // Flatten all calendar posts and sort descending by date (real IG feels)
    const all = CALENDAR_MONTHS.flatMap((m) => m.posts).map(toIgPost);
    return all.sort((a, b) => (a.date < b.date ? 1 : -1));
  }, []);

  // Reflect real feed size in the profile stats
  const profileStats = { ...IG_PROFILE.stats, posts: filtered.length };

  return (
    <div className="ig-page">
      <div className="ig-header-bar">
        <Link to="/" className="ig-back" data-testid="ig-back">
          <ArrowLeft size={16} /> torna al sito
        </Link>
        <span className="ig-note font-caveat">mockup del profilo · @lempole.wine</span>
        <Link to="/editorial-calendar" className="ig-back" data-testid="ig-calendar-link">
          piano editoriale →
        </Link>
      </div>

      <div className="ig-frame">
        {/* Profile header */}
        <div className="ig-profile">
          <div className="ig-avatar">
            <img src={IG_PROFILE.avatar} alt="Lempole" />
          </div>
          <div className="ig-profile-info">
            <div className="ig-profile-top">
              <h1 className="ig-handle">{IG_PROFILE.handle}</h1>
              <button className="ig-follow">Segui</button>
              <button className="ig-message">Messaggio</button>
            </div>
            <div className="ig-stats">
              <span><b>{profileStats.posts}</b> post</span>
              <span><b>{profileStats.followers}</b> follower</span>
              <span><b>{profileStats.following}</b> seguiti</span>
            </div>
            <div className="ig-bio">
              <p className="ig-bio-name"><b>{IG_PROFILE.displayName}</b></p>
              <p className="ig-bio-category">{IG_PROFILE.category}</p>
              {IG_PROFILE.bioLines.map((line, i) => (
                <p key={i}>{line}</p>
              ))}
              <a href={IG_PROFILE.link.url} className="ig-bio-link" target="_blank" rel="noreferrer">
                🔗 {IG_PROFILE.link.label}
              </a>
            </div>
          </div>
        </div>

        {/* Highlights */}
        <div className="ig-highlights">
          {IG_HIGHLIGHTS.map((h, i) => (
            <div key={i} className="ig-highlight">
              <div className="ig-highlight-ring">
                <div className="ig-highlight-inner">
                  <BlogSketch slug={h.sketch} />
                </div>
              </div>
              <span className="ig-highlight-label">{h.label}</span>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="ig-tabs">
          <button
            className={`ig-tab ${tab === 'grid' ? 'is-active' : ''}`}
            onClick={() => setTab('grid')}
          >
            <Grid3x3 size={14} /> Post
          </button>
          <button
            className={`ig-tab ${tab === 'reels' ? 'is-active' : ''}`}
            onClick={() => setTab('reels')}
          >
            <Play size={14} /> Reels
          </button>
          <button
            className={`ig-tab ${tab === 'tagged' ? 'is-active' : ''}`}
            onClick={() => setTab('tagged')}
          >
            <Tag size={14} /> Taggati
          </button>
        </div>

        {/* Grid */}
        {tab === 'grid' ? (
          <div className="ig-grid" data-testid="ig-grid">
            {filtered.map((p) => (
              <PostThumb key={p.id} post={p} onOpen={setActive} />
            ))}
          </div>
        ) : (
          <div className="ig-empty">
            <p className="font-display-italic">Ancora nessun contenuto qui.</p>
            <p className="text-sm opacity-70 mt-2">Reels e taggati arriveranno con i primi eventi della vendemmia 2026.</p>
          </div>
        )}
      </div>

      {/* Legend / Content strategy */}
      <aside className="ig-legend">
        <h2 className="font-display">Strategia editoriale</h2>
        <p className="ig-legend__intro">
          Il feed è direttamente collegato al piano editoriale in vigore
          (agosto → ottobre 2026). 27 post distribuiti su quattro pilastri,
          alternando fotografie del vigneto, illustrazioni ad acquerello e
          citazioni tipografiche. Cadenza: due post a settimana, con la
          vendemmia che concentra il ritmo nei giorni di settembre.
        </p>
        <ul className="ig-pillars">
          <li>
            <span className="dot" style={{ background: '#a44a30' }} />
            <b>Manifesto</b> · perché esistiamo, cosa promettiamo (bradipo, slow wine).
          </li>
          <li>
            <span className="dot" style={{ background: '#7a3520' }} />
            <b>Terroir</b> · Lamole, terrazzamenti, biodiversità, calendario agricolo.
          </li>
          <li>
            <span className="dot" style={{ background: '#7f8a72' }} />
            <b>Vigneron</b> · Raffaello, mani, gesti, famiglia, vendemmia.
          </li>
          <li>
            <span className="dot" style={{ background: '#4a4038' }} />
            <b>Prodotto</b> · Lempole Rosato, cantina, edizione limitata.
          </li>
        </ul>
        <p className="ig-legend__note font-caveat">
          I post-fotografia usano gli asset del sito. Le illustrazioni sono già
          disponibili nella libreria BlogSketch. Nulla di nuovo da produrre per la
          fase 1.
        </p>
      </aside>

      {active && <PostModal post={active} onClose={() => setActive(null)} />}
    </div>
  );
}
